// src/services/portal.ts
/**
 * Service para operaciones del Portal Cliente.
 */
import { api } from './api';
import type {
  PortalConfig,
  PortalDashboard,
  PortalTicket,
  PortalTicketCreate,
  PortalComentario,
  PortalComentarioCreate,
  PortalConversacion,
  PortalConversacionDetalle,
  PortalConversacionCreate,
  PortalMensajeCreate,
  PortalMensaje,
  PortalArticulo,
  PortalArticuloList,
  PortalCategoria,
  PortalPerfil,
  PortalPerfilUpdate,
  PortalCambiarPassword,
  PortalEmpresaResumen,
  PortalListResponse,
} from '@/types';

export const portalService = {
  // Config y Dashboard
  getConfig: () => api.get<PortalConfig>('/portal/config'),

  getDashboard: () => api.get<PortalDashboard>('/portal/dashboard'),

  getEmpresas: () => api.get<PortalEmpresaResumen[]>('/portal/empresas'),

  // Tickets
  listTickets: (params?: { empresa_id?: string; estado?: string; skip?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.empresa_id) searchParams.set('empresa_id', params.empresa_id);
    if (params?.estado) searchParams.set('estado', params.estado);
    if (params?.skip) searchParams.set('skip', String(params.skip));
    if (params?.limit) searchParams.set('limit', String(params.limit));

    const query = searchParams.toString();
    return api.get<PortalListResponse<PortalTicket>>(`/portal/tickets${query ? `?${query}` : ''}`);
  },

  getTicket: (id: string) => api.get<PortalTicket>(`/portal/tickets/${id}`),

  createTicket: (data: PortalTicketCreate) => api.post<PortalTicket>('/portal/tickets', data),

  getComentarios: (ticketId: string) =>
    api.get<PortalComentario[]>(`/portal/tickets/${ticketId}/comentarios`),

  addComentario: (ticketId: string, data: PortalComentarioCreate) =>
    api.post<PortalComentario>(`/portal/tickets/${ticketId}/comentarios`, data),

  // Conversaciones
  listConversaciones: (params?: { empresa_id?: string; estado?: string; skip?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.empresa_id) searchParams.set('empresa_id', params.empresa_id);
    if (params?.estado) searchParams.set('estado', params.estado);
    if (params?.skip) searchParams.set('skip', String(params.skip));
    if (params?.limit) searchParams.set('limit', String(params.limit));

    const query = searchParams.toString();
    return api.get<PortalListResponse<PortalConversacion>>(`/portal/conversaciones${query ? `?${query}` : ''}`);
  },

  getConversacion: (id: string) => api.get<PortalConversacionDetalle>(`/portal/conversaciones/${id}`),

  createConversacion: (data: PortalConversacionCreate) =>
    api.post<PortalConversacionDetalle>('/portal/conversaciones', data),

  sendMensaje: (conversacionId: string, data: PortalMensajeCreate) =>
    api.post<PortalMensaje>(`/portal/conversaciones/${conversacionId}/mensajes`, data),

  // Conocimiento
  searchArticulos: (params?: { query?: string; categoria_id?: string; skip?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.query) searchParams.set('query', params.query);
    if (params?.categoria_id) searchParams.set('categoria_id', params.categoria_id);
    if (params?.skip) searchParams.set('skip', String(params.skip));
    if (params?.limit) searchParams.set('limit', String(params.limit));

    const query = searchParams.toString();
    return api.get<PortalListResponse<PortalArticuloList>>(`/portal/conocimiento${query ? `?${query}` : ''}`);
  },

  getCategorias: () => api.get<PortalCategoria[]>('/portal/conocimiento/categorias'),

  getArticulo: (id: string) => api.get<PortalArticulo>(`/portal/conocimiento/${id}`),

  markArticuloUtil: (id: string, util: boolean) =>
    api.post<void>(`/portal/conocimiento/${id}/feedback?util=${util}`, null),

  // Perfil
  getPerfil: () => api.get<PortalPerfil>('/portal/perfil'),

  updatePerfil: (data: PortalPerfilUpdate) => api.put<PortalPerfil>('/portal/perfil', data),

  changePassword: (data: PortalCambiarPassword) => api.put<void>('/portal/perfil/password', data),
};
